// Platform IO boundary (load). Replaces the reference file-drop loaders: takes
// the worker's /secpose-assets bundle and populates the store the same way
// loader.load() used to — but from HTTP data, never local File[].
//
// Platform secPose always runs as v2. Historical og_estimations.toon content
// may be normalized into a read-only prediction seed, but it never switches
// the editor, drafts, or submit contract back to v1.

import { buildNullPredictions } from '../loaders/altogether/synthesizePredictions';
import { parseCalibrationBlob } from '../loaders/calibration';
import { type ParsedNewPredictions, parseNewPredictionsBlob } from '../loaders/image/parser';
import { useStore } from '../store';
import type { Side } from '../store/predictions';
import type { SideImageUrls, SideRawPredictions } from '../store/secpose';
import type { CalibrationData, EditMap, NewPredictionsFile } from '../types';
import { readDraft } from './drafts';
import { buildLegacyV2Seeds } from './legacySeed';

export type SecPoseCamera = 'head_front_left' | 'head_front_right' | 'head_left' | 'head_right';

export interface SecPoseImageInfo {
  frame_id: string;
  file_name: string;
  key: string;
  url: string;
}

export interface SecPoseAssetResolution {
  runtime_mode: 'v2';
  asset_mode: 'v2_native' | 'v2_with_legacy_seed';
  image_source: 'episode_v2';
  calibration_source: 'episode' | 'legacy_serial';
  pose_seed_source: 'blank' | 'legacy_og_estimations';
  display_frame_count: number;
  sampled_image_frame_count: number;
  toon_frame_count: number | null;
  matching_seed_frame_count: number | null;
  migration_status: 'not_needed' | 'manual_review';
  validation_warnings: string[];
}

export interface SecPoseAssetsResponse {
  episode_id: string;
  source_key: string;
  output_key: string;
  /** Legacy field retained for wire compatibility. Ignored by the frontend. */
  estimations_toon: string | null;
  metadata: unknown | null;
  calibration: unknown;
  calibration_file_name: string;
  /** Worker diagnostic; absent only when talking to an older deployment. */
  calibration_source?: 'episode' | 'legacy_serial';
  device_type: number | null;
  cameras: SecPoseCamera[];
  images: Record<SecPoseCamera, SecPoseImageInfo[]>;
  frame_count: number;
  /** Absent only when talking to a deployment older than the compatibility resolver. */
  asset_resolution?: SecPoseAssetResolution;
}

export type SecPosePoseSeedMode = 'blank' | 'legacy_v1';

export interface SecPoseLoadResult {
  availableSides: Side[];
  poseSeedSource: SecPosePoseSeedMode;
  warnings: string[];
}

const CAMERA_TO_SIDE: Record<SecPoseCamera, Side> = {
  head_front_left: 'front_left',
  head_front_right: 'front_right',
  head_left: 'left',
  head_right: 'right',
};

const ALL_SIDES: Side[] = ['front_left', 'front_right', 'left', 'right'];

function emptyEdits(): {
  front_left: EditMap;
  front_right: EditMap;
  left: EditMap;
  right: EditMap;
} {
  return { front_left: {}, front_right: {}, left: {}, right: {} };
}

async function parseCalibration(raw: unknown): Promise<CalibrationData | null> {
  if (raw == null) return null;
  try {
    const blob = new Blob([JSON.stringify(raw)], { type: 'application/json' });
    return await parseCalibrationBlob(blob);
  } catch (err) {
    console.warn('secPose calibration parse failed; epipolar disabled:', err);
    return null;
  }
}

function frameIdsForCamera(assets: SecPoseAssetsResponse, camera: SecPoseCamera): number[] {
  return [
    ...new Set(
      (assets.images[camera] ?? [])
        .map((img) => Number(img.frame_id))
        .filter((idx) => Number.isFinite(idx) && Number.isInteger(idx) && idx >= 0)
    ),
  ].sort((a, b) => a - b);
}

function metadataImageSize(raw: unknown): [number, number] | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const candidates = [record.image_size, record.imageSize];
  for (const value of candidates) {
    if (
      Array.isArray(value) &&
      value.length === 2 &&
      typeof value[0] === 'number' &&
      typeof value[1] === 'number' &&
      value[0] > 0 &&
      value[1] > 0
    ) {
      return [value[0], value[1]];
    }
  }
  const height = record.height ?? record.image_height ?? record.imageHeight;
  const width = record.width ?? record.image_width ?? record.imageWidth;
  if (typeof height === 'number' && height > 0 && typeof width === 'number' && width > 0) {
    return [height, width];
  }
  return null;
}

function metadataFps(raw: unknown): number | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const value = (raw as Record<string, unknown>).fps;
  return typeof value === 'number' && value > 0 ? value : undefined;
}

async function parseGeneratedPredictions(raw: NewPredictionsFile): Promise<ParsedNewPredictions> {
  const blob = new Blob([JSON.stringify(raw)], { type: 'application/json' });
  return parseNewPredictionsBlob(blob);
}

async function readExistingDraft(episodeId: string) {
  try {
    return await readDraft(episodeId);
  } catch (err) {
    console.warn('secPose draft read failed:', err);
    return null;
  }
}

/**
 * Parse the assets bundle and push everything into the store so AnnotateView
 * can render. Returns the sides that actually loaded.
 */
export async function loadSecPoseAssets(assets: SecPoseAssetsResponse): Promise<SecPoseLoadResult> {
  const store = useStore.getState();
  const annotationMode: 'v2' = 'v2';
  const existingDraft = await readExistingDraft(assets.episode_id);
  const warnings = [...(assets.asset_resolution?.validation_warnings ?? [])];
  const frameIdsByCamera = Object.fromEntries(
    (Object.keys(CAMERA_TO_SIDE) as SecPoseCamera[]).map((camera) => [
      camera,
      frameIdsForCamera(assets, camera),
    ])
  ) as Record<SecPoseCamera, number[]>;
  const workerAllowsLegacySeed =
    assets.asset_resolution?.pose_seed_source === 'legacy_og_estimations' &&
    typeof assets.estimations_toon === 'string' &&
    assets.estimations_toon.trim().length > 0;
  const draftAllowsLegacySeed = !existingDraft || existingDraft.poseSeedSource === 'legacy_v1';
  if (existingDraft?.poseSeedSource === 'legacy_v1' && !workerAllowsLegacySeed) {
    throw new Error('SecPose legacy seed required by the saved draft is unavailable.');
  }
  if (workerAllowsLegacySeed && existingDraft && !draftAllowsLegacySeed) {
    warnings.push('legacy_seed_disabled_for_pre_compatibility_draft');
  }
  const legacySeeds =
    workerAllowsLegacySeed && draftAllowsLegacySeed
      ? await buildLegacyV2Seeds(assets.estimations_toon!, frameIdsByCamera)
      : { cameras: {}, warnings: [] };
  warnings.push(...legacySeeds.warnings);

  // 1. Per camera -> synthesize v2 FrameMap from sampled images, optionally
  // using a validated legacy prediction as the read-only baseline.
  const rawPredictions: SideRawPredictions = {};
  const initialEdits = emptyEdits();
  const legacySeedFrames: Partial<Record<Side, number[]>> = {};
  const availableSides: Side[] = [];
  let meta: { fps: number; totalFrames: number; imageSize: [number, number] } | null = null;
  const defaultImageSize: [number, number] = metadataImageSize(assets.metadata) ?? [1, 1];
  const defaultFps = metadataFps(assets.metadata);

  for (const camera of Object.keys(CAMERA_TO_SIDE) as SecPoseCamera[]) {
    const side = CAMERA_TO_SIDE[camera];
    const frameIds = frameIdsByCamera[camera];
    if (frameIds.length === 0) {
      store.setPredictions(side, null);
      continue;
    }

    let parsedSide: ParsedNewPredictions;
    try {
      const legacy = legacySeeds.cameras[camera];
      parsedSide = await parseGeneratedPredictions(
        legacy?.predictions ?? buildNullPredictions(frameIds, defaultImageSize, defaultFps)
      );
      if (legacy) legacySeedFrames[side] = legacy.seededFrames;
    } catch (err) {
      throw new Error(
        `secPose v2 "${camera}" predictions failed to synthesize: ${
          err instanceof Error ? err.message : String(err)
        }`,
        { cause: err }
      );
    }
    store.setPredictions(side, parsedSide.frameMap);
    rawPredictions[side] = parsedSide.raw;
    // Legacy predictions are source seeds, never current-operator edits.
    initialEdits[side] = {};
    availableSides.push(side);
    // Front-left is the canonical meta source; fall back to whatever loads first.
    if (camera === 'head_front_left' || !meta) meta = parsedSide.meta;
  }

  if (availableSides.length === 0) {
    throw new Error('secPose images contained no recognized camera views');
  }
  if (!meta) throw new Error('secPose v2 had no usable metadata');

  // 2. Per-side frame→image-URL maps for ImageCanvas.
  const imageUrls: SideImageUrls = {};
  const frameSet = new Set<number>();
  for (const camera of assets.cameras) {
    const side = CAMERA_TO_SIDE[camera];
    if (!side) continue;
    const map: Record<number, string> = {};
    for (const img of assets.images[camera] ?? []) {
      const idx = Number(img.frame_id);
      if (!Number.isFinite(idx)) continue;
      map[idx] = img.url;
      frameSet.add(idx);
    }
    imageUrls[side] = map;
  }
  const navigableFrames = [...frameSet].sort((a, b) => a - b);

  // 3. Calibration (raw bundle → CalibrationData via reference parsers).
  const calibration = await parseCalibration(assets.calibration);

  // 4. Commit to the store.
  const poseSeedSource: SecPosePoseSeedMode =
    Object.keys(legacySeedFrames).length > 0 ? 'legacy_v1' : 'blank';
  store.setSecPoseData(rawPredictions, imageUrls, poseSeedSource, legacySeedFrames);
  store.setInitialEdits(initialEdits);
  store.clearHistory();
  store.setMeta(meta);
  store.setFolderMeta(meta);
  store.setCalibration(calibration);
  store.setNavigableFrames(navigableFrames);
  if (navigableFrames.length > 0) store.setCurrentFrame(navigableFrames[0]!);
  store.setLoadedSession({
    loaderId: 'secpose',
    name: assets.episode_id,
    files: {},
    raw: {},
    predictions: {
      front_left: null,
      front_right: null,
      left: null,
      right: null,
    },
    meta,
    initialEdits: emptyEdits(),
    navigableFrames,
    availableSides,
    calibration,
    signature: assets.episode_id,
    annotationMode,
  });

  store.setCompletionOpen(false);
  store.setAuditedFrames([]);
  store.resetEpisodeTimer();

  if (existingDraft) store.restoreFromDraft(existingDraft);

  store.startEpisodeTimer();
  store.setView('annotate');

  if (warnings.length > 0) {
    console.info('secPose asset compatibility diagnostics:', {
      episodeId: assets.episode_id,
      poseSeedSource,
      warnings,
    });
  }
  return { availableSides, poseSeedSource, warnings };
}

export { ALL_SIDES, CAMERA_TO_SIDE };
